// src/pages/DashboardPage.jsx
import { 
  Box, 
  Typography, 
  Button, 
  Avatar, 
  Badge, 
  IconButton,
  Paper,
  Divider,
  LinearProgress,
  Chip,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  CircularProgress,
  Menu, 
  MenuItem,
  Card,      
  CardContent 
} from '@mui/material';
import {
  Notifications,
  Home,
  Task as TaskIcon,
  ListAlt as MyTasksIcon,
  Payment as PaymentsIcon,
  AccountCircle as ProfileIcon,
  Forum as CommunityIcon,
  Help as HelpIcon,
  CameraAlt,
  Star,
  LocationOn,
  ExpandLess,
  ExpandMore,
  Logout,
  ArrowUpward,
  EmojiEvents,  
  MilitaryTech,  
  TrendingUp,    
  Whatshot       
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import DashboardLayout from '../components/DashboardLayout';

const top3Animation = `
  @keyframes blink {
    0% { opacity: 1; }
    50% { opacity: 0.6; }
    100% { opacity: 1; }
  }
`;

// Create a style component for the animation
const BlinkingListItem = Box;

export default function DashboardPage() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [earningsChartData, setEarningsChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);

 // NEW STATE VARIABLES FOR LEADERBOARD AND BADGES
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [userBadges, setUserBadges] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [badgesLoading, setBadgesLoading] = useState(true); // Fixed this line
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Helper function to explain ranking criteria
const getRankingExplanation = (userPosition) => {
  if (!userPosition) return '';
  
  const factors = [];
  
  if (userPosition.total_earnings > 0) {
    factors.push(`£${safeToFixed(userPosition.total_earnings)} earned`);
  }
  
  if (userPosition.tasks_completed > 0) {
    factors.push(`${userPosition.tasks_completed} tasks completed`);
  }
  
  if (userPosition.rating > 0) {
    factors.push(`${safeToFixed(userPosition.rating, 1)} rating`);
  }
  
  if (userPosition.users_in_same_rank > 1) {
    factors.push(`tied with ${userPosition.users_in_same_rank - 1} others`);
  }
  
  return factors.join(' • ');
};

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
    handleMenuClose();
  };

  const handleProfileNavigate = () => {
    navigate('/profile');
    handleMenuClose();
  };


// Helper function to safely format numbers
const safeToFixed = (value, decimals = 2) => {
  if (value === null || value === undefined) return '0.00';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return isNaN(num) ? '0.00' : num.toFixed(decimals);
};

// Helper function to safely get number value
const safeNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined) return defaultValue;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return isNaN(num) ? defaultValue : num;
};

  // ProtectedRoute already handles authentication
  // This useEffect only fetches user profile data for the dashboard
useEffect(() => {
  const fetchUserProfile = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch('https://propertyearn.net/api/api/user/profile', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setUserData({
          name: userData.name || 'User',
          tier: userData.tier || 'Basic',
          progress: calculateTierProgress(userData.tier, userData.tasks_completed || 0),
          rating: userData.rating || 0,
          balance: userData.balance || 0,
          tasksCompleted: userData.tasks_completed || 0,
          weeklyGoal: { current: 5, target: 10 },
          earningsData: [
            { day: 'Mon', earnings: 40 },
            { day: 'Tue', earnings: 65 },
            { day: 'Wed', earnings: 50 },
            { day: 'Thu', earnings: 75 },
            { day: 'Fri', earnings: 60 },
            { day: 'Sat', earnings: 90 },
            { day: 'Sun', earnings: 30 }
          ],
          recentTasks: [
            { id: 1, title: "Property Photo Shoot", reward: 18.50, type: "photography", distance: "1.2mi" },
            { id: 2, title: "Rental Price Verification", reward: 12.00, type: "research", distance: "0.5mi" }
          ]
        });
      } else if (response.status === 500) {
        console.error('Server error fetching profile');
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
    } finally {
      setLoading(false);
    }
  };

  fetchUserProfile();
}, [navigate]);

  // Helper function for tier progress calculation
  const calculateTierProgress = (tier, tasksCompleted) => {
    const tierRequirements = {
      'Basic': 0,
      'Verified': 10,
      'Pro': 50
    };
    
    if (tier === 'Pro') return 100;
    
    const nextTier = tier === 'Basic' ? 'Verified' : 'Pro';
    const progress = (tasksCompleted / tierRequirements[nextTier]) * 100;
    return Math.min(progress, 100);
  };

  const handleTaskStart = async (taskId) => {
    console.log(`Starting task ${taskId}`);
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`https://propertyearn.net/api/api/tasks/${taskId}/assignment-status`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.ok) {
      const data = await response.json();
      if (data.is_assigned) {
        // User is assigned, go to execution
        navigate(`/task/${taskId}/execute`);
      } else {
        // User needs to see details first
        navigate(`/task/${taskId}`);
      }
    } else {
      // Fallback to detail page if status check fails
      navigate(`/task/${taskId}`);
    }
  } catch (err) {
    console.error('Error checking assignment status:', err);
    navigate(`/task/${taskId}`);
  }
};

// hook to fetch real chart data
useEffect(() => {
  const fetchWeeklyEarningsData = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      setChartLoading(true);
      const response = await fetch('https://propertyearn.net/api/api/earnings/weekly-chart', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const chartData = await response.json();
        
        // Transform the API response to match the chart component format
        const formattedData = chartData.weekly_data.map(day => ({
          day: day.day,
          earnings: day.earnings,
          fullDate: day.date,
          tasks: day.tasks_completed
        }));
        
        setEarningsChartData(formattedData);
      } else {
        console.error('Failed to fetch weekly earnings data');
        // Fallback to empty data instead of mock data
        setEarningsChartData([]);
      }
    } catch (err) {
      console.error('Error fetching weekly earnings:', err);
      // Fallback to empty data
      setEarningsChartData([]);
    } finally {
      setChartLoading(false);
    }
  };

  fetchWeeklyEarningsData();
}, []);


useEffect(() => {
  const fetchLeaderboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://propertyearn.net/api/api/leaderboard/top-earners', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLeaderboardData(data);
      } else {
        console.error('Failed to fetch leaderboard data');
      }
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const fetchUserBadges = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://propertyearn.net/api/api/user/badges', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUserBadges(data);
      } else {
        console.error('Failed to fetch badges data');
      }
    } catch (err) {
      console.error('Failed to fetch badges:', err);
    } finally {
      setBadgesLoading(false); // This should now work
    }
  };

  // Only fetch if user is logged in
  const token = localStorage.getItem('token');
  if (token) {
    fetchLeaderboardData();
    fetchUserBadges();
  }
}, []);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DashboardLayout user={user} userData={userData}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        {/* Left Column - 60% width on desktop */}
        <Box sx={{ width: { xs: '100%', md: '60%' } }}>
          {/* Enhanced Tier Progress */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {user?.tier || 'Basic'} Tier
              </Typography>
              <Chip 
                label={user?.tier || 'Basic'} 
                color={
                  user?.tier === 'Pro' ? 'secondary' : 
                  user?.tier === 'Verified' ? 'primary' : 'default'
                }
                size="small"
              />
            </Box>
            
            {/* Progress to next tier */}
            {userData?.nextTier && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Progress to {userData.nextTier.name}: {user?.tasks_completed || 0}/{userData.nextTier.requiredTasks} tasks
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={userData.nextTier.progress}
                  sx={{ height: 10, borderRadius: 5, mb: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {userData.nextTier.tasksRemaining} more tasks to unlock higher earnings
                </Typography>
              </Box>
            )}
            
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/tiers')}
              startIcon={<ArrowUpward />}
            >
              View Tier Benefits
            </Button>
          </Paper>

            {/* Tier Upgrade Card */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#E8F5E8', border: '1px solid #4CAF50' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" color="#2E7D32" gutterBottom>
                      Tier Progress
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Unlock higher earning potential by completing tasks
                    </Typography>
                  </Box>
                  <Button
                      variant="contained"
                      onClick={() => navigate('/tiers')}
                      startIcon={<EmojiEvents />}  
                      sx={{
                        backgroundColor: '#4CAF50',
                        '&:hover': {
                          backgroundColor: '#388E3C'
                        }
                      }}
                    >
                      View Tiers
                    </Button>
                </Box>
              </Paper>

          {/* Leaderboard & Achievements Section */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
            
            {/* Leaderboard Card - Left Side */}
            <Paper sx={{ p: 2, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmojiEvents color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Top Earners
                </Typography>
              </Box>

              {leaderboardLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : leaderboardData ? (
                <Box>
                  {/* Current User Position */}
                  {leaderboardData.user_position && (
  <Paper sx={{ 
    mb: 2, 
    p: 2, 
    backgroundColor: 'primary.light', 
    color: 'white',
    background: 'linear-gradient(135deg, #17a74cff 0%, #17a74cff 100%)',
    position: 'relative',
    overflow: 'hidden'
  }}>
    {/* Subtle animated background */}
    <Box sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
      animation: 'shimmer 3s ease-in-out infinite',
      '@keyframes shimmer': {
        '0%': { transform: 'translateX(-100%)' },
        '100%': { transform: 'translateX(100%)' }
      }
    }} />
    
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      {/* Main Rank Display */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Your Global Rank
          </Typography>
          <Typography variant="h6" fontWeight="bold">
            #{safeNumber(leaderboardData.user_position.user_rank)}
            
          </Typography>
        </Box>
        
        {/* Percentile Badge */}
        {leaderboardData.user_position.user_rank > 0 && leaderboardData.user_position.total_users > 1 && (
          <Chip 
            label={`Top ${Math.round((leaderboardData.user_position.user_rank / leaderboardData.user_position.total_users) * 100)}%`}
            size="small"
            variant="outlined"
            sx={{ 
              color: 'white', 
              borderColor: 'rgba(255,255,255,0.5)',
              backgroundColor: 'rgba(255,255,255,0.1)',
              fontWeight: 'bold'
            }}
          />
        )}
      </Box>

      {/* Earnings and Tasks Summary */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2">
          £{safeToFixed(leaderboardData.user_position.total_earnings)} earned
        </Typography>
        <Typography variant="body2">
          {safeNumber(leaderboardData.user_position.tasks_completed)} tasks
        </Typography>
        <Typography variant="body2">
          ⭐ {safeToFixed(leaderboardData.user_position.rating, 1)} rating
        </Typography>
      </Box>

      {/* Tie Context Information - Only show if relevant */}
      {leaderboardData.user_position.users_in_same_rank > 1 && (
        <Box sx={{ 
          mt: 1, 
          pt: 1, 
          borderTop: '1px solid rgba(255,255,255,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography variant="caption" sx={{ opacity: 0.8, fontStyle: 'italic' }}>
            🏆 Tied with {leaderboardData.user_position.users_in_same_rank - 1} other user(s)
          </Typography>
        </Box>
      )}

    </Box>
  </Paper>
                    )}

             {/* Top 5 Leaderboard */}
                <List dense>
                  {leaderboardData.top_earners && leaderboardData.top_earners.slice(0, 5).map((earner, index) => (
                    <ListItem 
                      key={earner.id}
                      sx={{ 
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 1.5,
                        position: 'relative',
                        overflow: 'hidden',
                        // Sparkling border effect for top 3
                        ...(index < 3 && {
                          border: '2px solid transparent',
                          background: `
                            linear-gradient(45deg, 
                              ${index === 0 ? '#FFD700, #FFA500, #FFFF00' : 
                                index === 1 ? '#C0C0C0, #E8E8E8, #FFFFFF' : 
                                '#CD7F32, #D2691E, #8B4513'}
                            )`,
                          backgroundSize: '400% 400%',
                          animation: 'sparkleBorder 3s ease infinite',
                          borderRadius: 2,
                          mx: -0.5,
                          '@keyframes sparkleBorder': {
                            '0%': {
                              backgroundPosition: '0% 50%',
                              borderColor: index === 0 ? '#FFD700' : 
                                          index === 1 ? '#C0C0C0' : 
                                          '#CD7F32'
                            },
                            '50%': {
                              backgroundPosition: '100% 50%',
                              borderColor: index === 0 ? '#FFFF00' : 
                                          index === 1 ? '#FFFFFF' : 
                                          '#D2691E',
                              boxShadow: index === 0 ? '0 0 20px rgba(255, 215, 0, 0.6)' :
                                        index === 1 ? '0 0 20px rgba(192, 192, 192, 0.6)' :
                                        '0 0 20px rgba(205, 127, 50, 0.6)'
                            },
                            '100%': {
                              backgroundPosition: '0% 50%',
                              borderColor: index === 0 ? '#FFD700' : 
                                          index === 1 ? '#C0C0C0' : 
                                          '#CD7F32'
                            }
                          }
                        })
                      }}
                    >
                      {/* Sparkling background overlay */}
                      {index < 3 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: index === 0 ? 
                              'radial-gradient(circle at 20% 80%, rgba(255,215,0,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,0,0.1) 0%, transparent 50%)' :
                              index === 1 ?
                              'radial-gradient(circle at 20% 80%, rgba(192,192,192,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)' :
                              'radial-gradient(circle at 20% 80%, rgba(205,127,50,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(210,105,30,0.1) 0%, transparent 50%)',
                            animation: 'sparkleOverlay 4s ease-in-out infinite',
                            '@keyframes sparkleOverlay': {
                              '0%, 100%': { opacity: 0.3 },
                              '50%': { opacity: 0.8 }
                            },
                            pointerEvents: 'none'
                          }}
                        />
                      )}

                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          width: '100%',
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        {/* Animated Rank Badge with Sparkle Effect */}
                        <Box sx={{ position: 'relative', mr: 2 }}>
                          <Chip
                            label={`#${index + 1}`}
                            size="small"
                            sx={{ 
                              minWidth: 40,
                              fontWeight: 'bold',
                              fontSize: '0.9rem',
                              backgroundColor: 
                                index === 0 ? '#FFD700' :
                                index === 1 ? '#C0C0C0' :
                                index === 2 ? '#CD7F32' : 'primary.light',
                              color: index < 3 ? 'white' : 'inherit',
                              // Sparkling badge animation
                              ...(index < 3 && {
                                animation: 'badgeSparkle 2s ease-in-out infinite',
                                boxShadow: index === 0 ? 
                                  '0 0 15px rgba(255, 215, 0, 0.8), inset 0 0 10px rgba(255, 255, 255, 0.3)' :
                                  index === 1 ?
                                  '0 0 15px rgba(192, 192, 192, 0.8), inset 0 0 10px rgba(255, 255, 255, 0.3)' :
                                  '0 0 15px rgba(205, 127, 50, 0.8), inset 0 0 10px rgba(255, 255, 255, 0.3)',
                                '@keyframes badgeSparkle': {
                                  '0%, 100%': { 
                                    transform: 'scale(1) rotate(0deg)',
                                    boxShadow: index === 0 ? 
                                      '0 0 15px rgba(255, 215, 0, 0.8), inset 0 0 10px rgba(255, 255, 255, 0.3)' :
                                      index === 1 ?
                                      '0 0 15px rgba(192, 192, 192, 0.8), inset 0 0 10px rgba(255, 255, 255, 0.3)' :
                                      '0 0 15px rgba(205, 127, 50, 0.8), inset 0 0 10px rgba(255, 255, 255, 0.3)'
                                  },
                                  '25%': { 
                                    transform: 'scale(1.1) rotate(5deg)',
                                    boxShadow: index === 0 ? 
                                      '0 0 25px rgba(255, 215, 0, 1), inset 0 0 15px rgba(255, 255, 255, 0.5)' :
                                      index === 1 ?
                                      '0 0 25px rgba(192, 192, 192, 1), inset 0 0 15px rgba(255, 255, 255, 0.5)' :
                                      '0 0 25px rgba(205, 127, 50, 1), inset 0 0 15px rgba(255, 255, 255, 0.5)'
                                  },
                                  '50%': { 
                                    transform: 'scale(1.05) rotate(-5deg)',
                                    boxShadow: index === 0 ? 
                                      '0 0 30px rgba(255, 255, 0, 1), inset 0 0 20px rgba(255, 255, 255, 0.6)' :
                                      index === 1 ?
                                      '0 0 30px rgba(255, 255, 255, 1), inset 0 0 20px rgba(255, 255, 255, 0.6)' :
                                      '0 0 30px rgba(210, 105, 30, 1), inset 0 0 20px rgba(255, 255, 255, 0.6)'
                                  },
                                  '75%': { 
                                    transform: 'scale(1.1) rotate(3deg)',
                                    boxShadow: index === 0 ? 
                                      '0 0 20px rgba(255, 165, 0, 1), inset 0 0 12px rgba(255, 255, 255, 0.4)' :
                                      index === 1 ?
                                      '0 0 20px rgba(230, 230, 230, 1), inset 0 0 12px rgba(255, 255, 255, 0.4)' :
                                      '0 0 20px rgba(184, 115, 51, 1), inset 0 0 12px rgba(255, 255, 255, 0.4)'
                                  }
                                }
                              })
                            }}
                          />
                          
                          {/* Sparkle particles around badge */}
                          {index < 3 && (
                            <>
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: -5,
                                  right: -5,
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: index === 0 ? 
                                    'radial-gradient(circle, #FFFF00, #FFD700)' :
                                    index === 1 ?
                                    'radial-gradient(circle, #FFFFFF, #C0C0C0)' :
                                    'radial-gradient(circle, #FFA500, #CD7F32)',
                                  animation: 'sparkleParticle 2s ease-in-out infinite',
                                  '@keyframes sparkleParticle': {
                                    '0%, 100%': { opacity: 0, transform: 'scale(0) rotate(0deg)' },
                                    '50%': { opacity: 1, transform: 'scale(1.2) rotate(180deg)' }
                                  }
                                }}
                              />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: -5,
                                  left: -5,
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  background: index === 0 ? 
                                    'radial-gradient(circle, #FFA500, #FFD700)' :
                                    index === 1 ?
                                    'radial-gradient(circle, #E8E8E8, #C0C0C0)' :
                                    'radial-gradient(circle, #D2691E, #CD7F32)',
                                  animation: 'sparkleParticle 2s ease-in-out infinite 0.5s',
                                  '@keyframes sparkleParticle': {
                                    '0%, 100%': { opacity: 0, transform: 'scale(0) rotate(0deg)' },
                                    '50%': { opacity: 1, transform: 'scale(1.2) rotate(180deg)' }
                                  }
                                }}
                              />
                            </>
                          )}
                        </Box>
                        
                        {/* User Info with Glowing Text */}
                        <Box sx={{ flex: 1 }}>
                          <Typography 
                            variant="body2" 
                            fontWeight="bold"
                            sx={{
                              ...(index < 3 && {
                                background: index === 0 ? 
                                  'linear-gradient(45deg, #FFD700, #FFFF00, #FFA500)' :
                                  index === 1 ?
                                  'linear-gradient(45deg, #C0C0C0, #FFFFFF, #E8E8E8)' :
                                  'linear-gradient(45deg, #CD7F32, #D2691E, #8B4513)',
                                backgroundSize: '200% 200%',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                color: 'transparent',
                                animation: 'textShine 3s ease infinite',
                                '@keyframes textShine': {
                                  '0%': { backgroundPosition: '0% 50%' },
                                  '50%': { backgroundPosition: '100% 50%' },
                                  '100%': { backgroundPosition: '0% 50%' }
                                }
                              })
                            }}
                          >
                            {earner.name || 'Anonymous'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {earner.tier || 'Basic'} • {safeNumber(earner.tasks_completed)} tasks
                          </Typography>
                        </Box>
                        
                        {/* Animated Earnings with Money Effect */}
                        <Typography 
                          variant="body2" 
                          fontWeight="bold" 
                          sx={{
                            ...(index < 3 ? {
                              background: 'linear-gradient(45deg, #4CAF50, #8BC34A, #CDDC39)',
                              backgroundSize: '200% 200%',
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              color: 'transparent',
                              animation: 'moneyGlow 2s ease-in-out infinite',
                              textShadow: '0 0 10px rgba(76, 175, 80, 0.3)',
                              padding: '2px 8px',
                              borderRadius: 1,
                              border: '1px solid rgba(76, 175, 80, 0.3)',
                              '@keyframes moneyGlow': {
                                '0%, 100%': { 
                                  backgroundPosition: '0% 50%',
                                  boxShadow: '0 0 5px rgba(76, 175, 80, 0.3)'
                                },
                                '50%': { 
                                  backgroundPosition: '100% 50%',
                                  boxShadow: '0 0 15px rgba(76, 175, 80, 0.6), 0 0 25px rgba(76, 175, 80, 0.4)'
                                }
                              }
                            } : {
                              color: 'primary'
                            })
                          }}
                        >
                          £{safeToFixed(earner.total_earnings)}
                        </Typography>
                      </Box>

                      {/* Floating sparkles */}
                      {index < 3 && (
                        <>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '20%',
                              right: '10%',
                              width: 4,
                              height: 4,
                              borderRadius: '50%',
                              background: 'rgba(255, 255, 255, 0.8)',
                              animation: 'floatSparkle 3s ease-in-out infinite',
                              '@keyframes floatSparkle': {
                                '0%, 100%': { opacity: 0, transform: 'translateY(0px) scale(0)' },
                                '25%': { opacity: 1, transform: 'translateY(-10px) scale(1)' },
                                '50%': { opacity: 0.5, transform: 'translateY(-20px) scale(1.2)' },
                                '75%': { opacity: 0, transform: 'translateY(-30px) scale(0)' }
                              }
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '60%',
                              left: '15%',
                              width: 3,
                              height: 3,
                              borderRadius: '50%',
                              background: 'rgba(255, 255, 255, 0.6)',
                              animation: 'floatSparkle 3s ease-in-out infinite 1s',
                              '@keyframes floatSparkle': {
                                '0%, 100%': { opacity: 0, transform: 'translateY(0px) scale(0)' },
                                '25%': { opacity: 1, transform: 'translateY(-8px) scale(1)' },
                                '50%': { opacity: 0.5, transform: 'translateY(-16px) scale(1.2)' },
                                '75%': { opacity: 0, transform: 'translateY(-24px) scale(0)' }
                              }
                            }}
                          />
                        </>
                      )}
                    </ListItem>
                  ))}
                </List>

                  {/* Empty state if no leaderboard data */}
                  {(!leaderboardData.top_earners || leaderboardData.top_earners.length === 0) && (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <EmojiEvents sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Complete tasks to appear on the leaderboard!
                      </Typography>
                    </Box>
                  )}

                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/leaderboard')}
                    sx={{ mt: 1 }}
                    disabled={!leaderboardData.top_earners || leaderboardData.top_earners.length === 0}
                  >
                    View Full Leaderboard
                  </Button>
                </Box>
              ) : (
                <Typography color="text.secondary" textAlign="center" py={2}>
                  Failed to load leaderboard
                </Typography>
              )}
            </Paper>

                {/* Badges Card - Right Side */}
                <Paper sx={{ p: 2, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <MilitaryTech color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Your Badges
                    </Typography>
                    {userBadges && (
                      <Chip 
                        label={userBadges.total_badges} 
                        size="small" 
                        color="primary" 
                        sx={{ ml: 'auto' }}
                      />
                    )}
                  </Box>

                  {badgesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : userBadges ? (
                    <Box>
                      {/* Earned Badges Grid */}
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', 
                        gap: 1,
                        mb: 2 
                      }}>
                        {userBadges.badges.slice(0, 6).map((badge) => (
                          <Paper 
                            key={badge.id}
                            sx={{ 
                              p: 1, 
                              textAlign: 'center',
                              backgroundColor: `${badge.color}.light`,
                              border: `2px solid`,
                              borderColor: `${badge.color}.main`,
                              minHeight: 70,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}
                          >
                            <Typography variant="h6" sx={{ mb: 0.5, fontSize: '1.5rem' }}>
                              {badge.icon}
                            </Typography>
                            <Typography variant="caption" sx={{ lineHeight: 1, fontWeight: 'bold' }}>
                              {badge.name}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>

                      {/* Next Available Badges */}
                      {userBadges.next_badges && userBadges.next_badges.length > 0 && (
                        <Box>
                          <Typography variant="body2" fontWeight="bold" gutterBottom>
                            Next Badges:
                          </Typography>
                          {userBadges.next_badges.map((badge, index) => (
                            <Box key={index} sx={{ mb: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="body2" sx={{ mr: 1, fontSize: '1.2rem' }}>
                                  {badge.icon}
                                </Typography>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" fontWeight="medium">
                                    {badge.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {badge.description}
                                  </Typography>
                                </Box>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={(badge.progress / badge.target) * 100}
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Progress: {badge.progress}/{badge.target}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {userBadges.badges.length > 6 && (
                        <Button
                          fullWidth
                          variant="outlined"
                          size="small"
                          onClick={() => navigate('/achievements')}
                          sx={{ mt: 1 }}
                        >
                          View All Badges ({userBadges.badges.length})
                        </Button>
                      )}

                      {/* Empty state for no badges */}
                      {userBadges.badges.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                          <MilitaryTech sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            Complete tasks to earn your first badge!
                          </Typography>
                          <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => navigate('/tasks')}
                            sx={{ mt: 1 }}
                          >
                            Browse Tasks
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography color="text.secondary" textAlign="center" py={2}>
                      Failed to load badges
                    </Typography>
                  )}
                </Paper>
              </Box>

          {/* Task Spotlight */}
          <Typography variant="h6" sx={{ mb: 1, mt: 3 }}>
            Recommended For You
          </Typography>
          {userData?.recentTasks?.map(task => (
            <Paper key={task.id} sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography fontWeight="bold">{task.title}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <LocationOn color="action" fontSize="small" />
                    <Typography variant="body2" sx={{ ml: 0.5 }}>
                      {task.distance} away
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography fontWeight="bold" color="primary">
                    £{task.reward.toFixed(2)}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ mt: 1 }}
                    startIcon={<TaskIcon />}
                    onClick={() => handleTaskStart(task.id)}
                  >
                    Start
                  </Button>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>

        {/* Right Column - 40% width on desktop */}
        <Box sx={{ width: { xs: '100%', md: '40%' } }}>
          {/* Earnings Graph */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}>
              <Typography variant="h6">Weekly Earnings</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {chartLoading && (
                  <CircularProgress size={20} />
                )}
                <IconButton size="small" onClick={() => setOpen(!open)}>
                  {open ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
            </Box>
            <Collapse in={open}>
              <Box sx={{ height: 250 }}>
                {chartLoading ? (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%' 
                  }}>
                    <CircularProgress />
                  </Box>
                ) : earningsChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={earningsChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="day" 
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `£${value}`}
                      />
                      <Tooltip 
                        formatter={(value) => [`£${value}`, "Earnings"]}
                        labelFormatter={(label, payload) => {
                          if (payload && payload[0]) {
                            return `Date: ${payload[0].payload.fullDate}`;
                          }
                          return label;
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="earnings"
                        stroke="#4CAF50"
                        strokeWidth={2}
                        dot={{ r: 4, fill: '#4CAF50' }}
                        activeDot={{ r: 6, fill: '#2E7D32' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%',
                    flexDirection: 'column',
                    color: 'text.secondary'
                  }}>
                    <Typography variant="body1" gutterBottom>
                      No earnings data for this week
                    </Typography>
                    <Typography variant="body2">
                      Complete tasks to see your earnings chart
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Paper>

          {/* Quick Stats */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Your Activity
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Box>
                <Typography variant="body2">Tasks Completed</Typography>
                <Typography fontWeight="bold">{user?.tasks_completed || 0}</Typography>
              </Box>
              <Box>
                <Typography variant="body2">Rating</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Star color="warning" fontSize="small" />
                  <Typography fontWeight="bold" sx={{ ml: 0.5 }}>
                    {(user?.rating || 0).toFixed(1)}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<PaymentsIcon />}
              onClick={() => navigate('/earnings')}
            >
              View Earnings
            </Button>
          </Paper>
        </Box>
      </Box>
    </DashboardLayout>
  );
}